// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditContact } from '@/audit/api/types';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Box, Stack, Typography } from '@mui/material';

interface ContactUpdate {
  id: string;
  label: string;
  oldValue: string | null;
  newValue: string | null;
}

interface Props {
  property: AuditContact;
}

export function AuditContactLabel(props: Props) {
  const updates: ContactUpdate[] = [
    {
      id: 'name',
      label: 'Name',
      oldValue: props.property.oldValue?.name ?? null,
      newValue: props.property.newValue?.name ?? null,
    },
    {
      id: 'company',
      label: 'Company',
      oldValue: props.property.oldValue?.company ?? null,
      newValue: props.property.newValue?.company ?? null,
    },
    {
      id: 'email',
      label: 'Email',
      oldValue: props.property.oldValue?.email ?? null,
      newValue: props.property.newValue?.email ?? null,
    },
    {
      id: 'phone',
      label: 'Phone',
      oldValue: props.property.oldValue?.phone ?? null,
      newValue: props.property.newValue?.phone ?? null,
    },
  ];

  // A create carries no prior contact, so each set field reads as a single
  // value rather than a "None → value" transition.
  if (props.property.oldValue === undefined) {
    const present = updates.filter((update) => update.newValue !== null);
    return (
      <Stack>
        <Typography component="span" variant="caption" fontWeight="bold">
          {props.property.name}:
        </Typography>
        {present.map(({ id, label, newValue }) => (
          <Typography key={id} variant="caption" marginLeft={2}>
            {label}: {newValue}
          </Typography>
        ))}
      </Stack>
    );
  }

  const isUnchanged = updates.every(
    (update) => update.oldValue === update.newValue
  );

  return (
    <Stack direction={isUnchanged ? 'row' : 'column'}>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>

      {isUnchanged ? (
        <Typography
          color="text.secondary"
          component="span"
          variant="caption"
          sx={{ ml: 0.5 }}
        >
          Not changed
        </Typography>
      ) : (
        <Box
          display="grid"
          columnGap={0.5}
          gridTemplateColumns="repeat(4, min-content)"
        >
          {updates.map(({ id, label, oldValue, newValue }) => {
            if (oldValue === newValue) {
              return null;
            }

            const oldValueLabel = oldValue ?? 'None';
            const oldValueColor =
              oldValue === null ? 'text.secondary' : 'text.primary';

            const newValueLabel = newValue ?? 'None';
            const newValueColor =
              newValue === null ? 'text.secondary' : 'text.primary';

            return (
              <Typography
                key={id}
                variant="caption"
                component="span"
                display="grid"
                alignItems="center"
                gridColumn="span 4"
                gridTemplateColumns="subgrid"
                marginLeft={2}
              >
                <Typography
                  component="span"
                  variant="inherit"
                  whiteSpace="nowrap"
                >
                  {label}:
                </Typography>
                <Typography
                  component="span"
                  variant="inherit"
                  whiteSpace="nowrap"
                  color={oldValueColor}
                >
                  {oldValueLabel}
                </Typography>
                <ArrowForwardRounded fontSize="inherit" />
                <Typography
                  component="span"
                  variant="inherit"
                  whiteSpace="nowrap"
                  color={newValueColor}
                >
                  {newValueLabel}
                </Typography>
              </Typography>
            );
          })}
        </Box>
      )}
    </Stack>
  );
}
